import React, { useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageConfig from '@/component/layout/PageConfig'
import BorderButton from '@/component/button/BorderButton'
import Pagination from '@/component/pagination/Pagination'
import { useQueryGetMindHealthReports } from '@/api/mentalHealth/mindHealthReport'
import useQueryGetUserDetail from '@/api/auth/getUser'
import { useAppLocale } from '@/locales/locale'

const PAGE_SIZE = 12

interface SessionRecord {
    sessionId: string
    analysisDate: string
    mindScore?: number
    decisionLabel?: string
    stressCategory?: string
    quality?: number
    isAnalyzed: boolean
}

const MentalHealthReports: React.FC = () => {
    const { t } = useTranslation()
    const { intlLocale } = useAppLocale()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [currentPage, setCurrentPage] = useState(1)

    // URL에서 userId 파라미터 가져오기
    const userIdFromUrl = searchParams.get('userId')
    const filterUserId = userIdFromUrl ? Number(userIdFromUrl) : 0

    const { data: userDetail } = useQueryGetUserDetail(filterUserId)
    const userName = userDetail?.name || ''

    const { data: reportsData, isLoading } = useQueryGetMindHealthReports(
        filterUserId,
        {
            pageNum: currentPage,
            pageSize: PAGE_SIZE,
            sortBy: 'measuredAt:desc'
        },
        filterUserId > 0
    )

    const pagination = reportsData?.pagination

    // 서버 리포트 목록 그대로. 요약이 인라인이라 행마다 불러올 필요가 없다.
    const records: SessionRecord[] = useMemo(() => {
        return (reportsData?.data || []).map((item) => ({
            sessionId: item.sessionId,
            analysisDate: item.measuredAt || item.computedAt || '',
            mindScore: item.summary.mindScore ?? undefined,
            decisionLabel: item.summary.decisionLabel ?? undefined,
            stressCategory: item.summary.stressCategory ?? undefined,
            quality: item.summary.analyzability ?? undefined,
            // 점수가 나온 회차만 지표를 보여준다. 측정 품질이 부족하면 값이 전부 null 이다.
            isAnalyzed: Number.isFinite(item.summary.mindScore)
        }))
    }, [reportsData])

    const handleViewDetail = (record: SessionRecord) => {
        navigate(`/mental-health/protectee/${filterUserId}/reports/${record.sessionId}`)
    }

    const getStatusColor = (label?: string) => {
        switch (label) {
            case 'healthy':
                return {
                    bg: 'bg-emerald-50',
                    text: 'text-emerald-700',
                    border: 'border-emerald-300',
                    icon: '✅',
                    gradient: 'from-emerald-400 to-teal-400'
                }
            case 'normal':
                return {
                    bg: 'bg-blue-50',
                    text: 'text-blue-700',
                    border: 'border-blue-300',
                    icon: '🔵',
                    gradient: 'from-blue-400 to-cyan-400'
                }
            case 'caution':
                return {
                    bg: 'bg-amber-50',
                    text: 'text-amber-700',
                    border: 'border-amber-300',
                    icon: '⚠️',
                    gradient: 'from-amber-400 to-orange-400'
                }
            case 'risk':
                return {
                    bg: 'bg-red-50',
                    text: 'text-red-700',
                    border: 'border-red-300',
                    icon: '🚨',
                    gradient: 'from-red-400 to-pink-400'
                }
            default:
                return {
                    bg: 'bg-slate-50',
                    text: 'text-slate-700',
                    border: 'border-slate-300',
                    icon: '📄',
                    gradient: 'from-indigo-400 to-violet-400'
                }
        }
    }

    const getStatusText = (label?: string) =>
        label ? t(`mentalHealth.status.${label}`) : t('mentalHealth.status.pending')

    const getStressText = (category?: string) =>
        category ? t(`mentalHealth.stress.${category}`) : '-'

    const getStressEmoji = (stress?: string) => {
        switch (stress) {
            case 'low':
                return '😌'
            case 'normal':
                return '😐'
            case 'high':
                return '😰'
            case 'very_high':
                return '😱'
            default:
                return '🤔'
        }
    }

    return (
        <PageConfig showTopNav={true} showSideNav={true}>
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
                {/* 헤더 */}
                <div className="bg-gradient-to-r from-teal-600 via-emerald-600 to-green-600 text-white">
                    <div className="max-w-7xl mx-auto px-6 py-10 sm:px-4 sm:py-6">
                        <div className="flex items-center justify-between gap-5 md:flex-col md:items-stretch">
                            <div>
                                <div className="flex items-center space-x-4 mb-3 sm:space-x-3">
                                    <div className="text-5xl sm:text-3xl">📚</div>
                                    <div>
                                        <h1 className="text-4xl font-black sm:text-2xl">
                                            {t('mentalHealth.reports.title.user', {
                                                name: userName
                                            })}
                                        </h1>
                                        <p className="text-emerald-100 text-lg mt-1 sm:text-sm">
                                            {t('mentalHealth.reports.total', {
                                                count: pagination?.totalEntries ?? 0
                                            })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-3 sm:flex-col">
                                <BorderButton
                                    text={t('mentalHealth.reports.button.back')}
                                    onClick={() => navigate('/mental-health')}
                                    className="!bg-transparent !text-white !border-white hover:!bg-white/10"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-6 py-8 sm:px-4 sm:py-5">
                    {/* 리포트 카드 그리드 */}
                    {isLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="text-center">
                                <div className="text-6xl mb-4 animate-pulse">⏳</div>
                                <p className="text-gray-600 font-medium">
                                    {t('common.loading')}...
                                </p>
                            </div>
                        </div>
                    ) : records.length > 0 ? (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                {records.map((record) => {
                                    const statusColors = getStatusColor(record.decisionLabel)
                                    return (
                                        <div
                                            key={record.sessionId}
                                            className={`bg-white rounded-3xl shadow-lg border-2 border-gray-100 overflow-hidden transition-all duration-300 sm:rounded-2xl ${
                                                record.isAnalyzed
                                                    ? 'hover:shadow-2xl hover:scale-105 cursor-pointer'
                                                    : 'opacity-70'
                                            }`}
                                            onClick={() => record.isAnalyzed && handleViewDetail(record)}
                                        >
                                            {/* 카드 헤더 */}
                                            <div className="bg-gray-200 border-b border-gray-300 p-6">
                                                <div className="flex items-center justify-between">
                                                    <div className="text-sm font-semibold text-gray-700">
                                                        {new Date(
                                                            record.analysisDate
                                                        ).toLocaleDateString(
                                                            intlLocale,
                                                            {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            }
                                                        )}
                                                    </div>
                                                    {record.isAnalyzed && (
                                                        <div className="text-3xl font-black text-gray-900">
                                                            {Math.round(record.mindScore || 0)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* 카드 본문 */}
                                            <div className="p-6">
                                                {record.isAnalyzed ? (
                                                    <>
                                                        <div className="grid grid-cols-2 gap-3 mb-4">
                                                            <div>
                                                                <div className="text-xs text-gray-500 mb-1">
                                                                    {t(
                                                                        'mentalHealth.modal.result.status'
                                                                    )}
                                                                </div>
                                                                <div
                                                                    className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-bold border-2 ${statusColors.bg} ${statusColors.text} ${statusColors.border}`}
                                                                >
                                                                    <span className="mr-1">
                                                                        {statusColors.icon}
                                                                    </span>
                                                                    {getStatusText(
                                                                        record.decisionLabel
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="text-xs text-gray-500 mb-1">
                                                                    {t('mentalHealth.stress.title')}
                                                                </div>
                                                                <div className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-gray-100 text-gray-700">
                                                                    <span className="mr-1">
                                                                        {getStressEmoji(
                                                                            record.stressCategory
                                                                        )}
                                                                    </span>
                                                                    {getStressText(
                                                                        record.stressCategory
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="bg-gray-50 rounded-xl p-3 mb-4">
                                                            <div className="text-xs text-gray-500 mb-2">
                                                                {t(
                                                                    'mentalHealth.reports.dataQuality'
                                                                )}
                                                            </div>
                                                            <div className="flex items-center">
                                                                <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                                                                    <div
                                                                        className="bg-emerald-500 h-2 rounded-full"
                                                                        style={{
                                                                            width: `${(record.quality || 0) * 100}%`
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                                <span className="text-sm font-bold text-gray-700">
                                                                    {Math.round(
                                                                        (record.quality || 0) * 100
                                                                    )}
                                                                    %
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="bg-gray-50 rounded-xl p-4 mb-4 text-center">
                                                        <div className="text-sm text-gray-500">
                                                            {t(
                                                                'mentalHealth.reports.notAnalyzed.desc'
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                <button
                                                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold py-3 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                                    disabled={!record.isAnalyzed}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleViewDetail(record)
                                                    }}
                                                >
                                                    {t('mentalHealth.reports.button.viewDetail')}
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>

                            {/* 페이지네이션 */}
                            {!!pagination && pagination.totalPages > 1 && (
                                <div className="flex justify-center">
                                    <Pagination
                                        current_page={pagination.currentPage}
                                        page_size={pagination.pageSize}
                                        total_page={pagination.totalPages}
                                        fetchPage={setCurrentPage}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="bg-white rounded-3xl shadow-lg p-16 text-center border-2 border-dashed border-gray-200">
                            <div className="text-8xl mb-6">📊</div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">
                                {t('mentalHealth.reports.empty')}
                            </h3>
                        </div>
                    )}
                </div>
            </div>
        </PageConfig>
    )
}

export default MentalHealthReports
